import { Dispatch, SetStateAction, useMemo } from 'react';

import { Column, ColumnFiltersState } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { PersonSummary } from '@/api/queries/useGetMatterDocPersons';
import { Doc } from '@/api/types/backend-dto-types';
import { DateRangeFilter } from '@/new/components/date-range-filter';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { PopoverContent } from '@/new/components/shadcn/popover';

// Extended Doc type with persons
type DocWithPersons = Doc & { persons?: PersonSummary[] };

export function FilterPopover({
  docs,
  typeColumn,
  extensionColumn,
  issuesColumn,
  dateColumn,
  relativityIssuesColumn,
  personsColumn,
  uploadDateColumn,
  setColumnFilters,
}: {
  docs: DocWithPersons[];
  typeColumn?: Column<DocWithPersons, unknown>;
  extensionColumn?: Column<DocWithPersons, unknown>;
  issuesColumn?: Column<DocWithPersons, unknown>;
  dateColumn?: Column<DocWithPersons, unknown>;
  relativityIssuesColumn?: Column<DocWithPersons, unknown>;
  personsColumn?: Column<DocWithPersons, unknown>;
  uploadDateColumn?: Column<DocWithPersons, unknown>;
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
}) {
  const { matterId } = useParams();
  if (!matterId) return null;

  // Extract date values from column filter
  const dateFilterValue = dateColumn?.getFilterValue() as { startDate: Date | null; endDate: Date | null } | undefined;
  const startDate = dateFilterValue?.startDate || null;
  const endDate = dateFilterValue?.endDate || null;

  // Update date filter through column API
  const setStartDate = (date: Date | null) => {
    if (dateColumn) {
      const currentValue = dateColumn.getFilterValue() as { startDate: Date | null; endDate: Date | null } | undefined;
      const currentEndDate = currentValue?.endDate || null;
      if (date || currentEndDate) {
        dateColumn.setFilterValue({ startDate: date, endDate: currentEndDate });
      } else {
        dateColumn.setFilterValue(undefined);
      }
    }
  };

  const setEndDate = (date: Date | null) => {
    if (dateColumn) {
      const currentValue = dateColumn.getFilterValue() as { startDate: Date | null; endDate: Date | null } | undefined;
      const currentStartDate = currentValue?.startDate || null;
      if (currentStartDate || date) {
        dateColumn.setFilterValue({ startDate: currentStartDate, endDate: date });
      } else {
        dateColumn.setFilterValue(undefined);
      }
    }
  };

  const fileTypes = useMemo(() => Array.from(new Set(docs.map((doc) => doc.file.extension))), [docs]);
  const docTypes = useMemo(() => Array.from(new Set(docs.map((doc) => doc.type ?? ''))), [docs]);
  const issues = useMemo(() => Array.from(new Set(docs.map((doc) => doc.issues).flat())), [docs]);

  // Extract Relativity metadata values
  const relativityIssues = useMemo(
    () =>
      Array.from(
        new Set(
          docs
            .map((doc) => doc.file.relativityMetadata?.issues ?? [])
            .flat()
            .filter((issue): issue is string => issue !== null && issue !== undefined),
        ),
      ),
    [docs],
  );

  const typeFilterValue = typeColumn?.getFilterValue();
  const selectedTypes = Array.isArray(typeFilterValue) ? typeFilterValue : [];

  const extensionFilterValue = extensionColumn?.getFilterValue();
  const selectedExtensions = Array.isArray(extensionFilterValue) ? extensionFilterValue : [];

  const issuesFilterValue = issuesColumn?.getFilterValue();
  const selectedIssues = Array.isArray(issuesFilterValue) ? issuesFilterValue : [];

  const relativityIssuesFilterValue = relativityIssuesColumn?.getFilterValue();
  const selectedRelativityIssues = Array.isArray(relativityIssuesFilterValue) ? relativityIssuesFilterValue : [];

  // Extract unique person names from all docs
  const personNames = useMemo(
    () =>
      Array.from(
        new Set(
          docs
            .map((doc) => doc.persons ?? [])
            .flat()
            .map((person) => person.name),
        ),
      ).sort(),
    [docs],
  );

  const personsFilterValue = personsColumn?.getFilterValue();
  const selectedPersons = Array.isArray(personsFilterValue) ? personsFilterValue : [];

  // Extract unique run date/times from all docs
  const runDateTimes = useMemo(() => {
    const formattedDates = docs
      .map((doc) => doc.file.createdDate)
      .filter((date) => date !== null && date !== undefined)
      .map((dateStr) => {
        return new Date(dateStr).toLocaleString('en-GB', {
          dateStyle: 'medium',
          timeStyle: 'short',
        });
      });

    // Get unique formatted dates
    const uniqueFormattedDates = Array.from(new Set(formattedDates));

    // Sort in descending order (most recent first) by parsing back to dates
    return uniqueFormattedDates.sort((a, b) => {
      // Parse the formatted strings back to compare
      const dateA = new Date(a.replace(/(\d+)\s(\w+)\s(\d+),\s(\d+):(\d+)/, '$1 $2 $3 $4:$5'));
      const dateB = new Date(b.replace(/(\d+)\s(\w+)\s(\d+),\s(\d+):(\d+)/, '$1 $2 $3 $4:$5'));
      return dateB.getTime() - dateA.getTime();
    });
  }, [docs]);

  const uploadDateFilterValue = uploadDateColumn?.getFilterValue();
  const selectedRunDateTimes = Array.isArray(uploadDateFilterValue) ? uploadDateFilterValue : [];

  return (
    <PopoverContent side="left" className="mt-12 min-w-md" sideOffset={8}>
      <div className="-mx-4 -mt-4 rounded-t-md border-b bg-gray-50 p-4 font-medium text-gray-700">Filters</div>

      <div className="mt-4">
        {extensionColumn && (
          <>
            <p className="my-3 text-gray-600">File Extension</p>
            <FilterMultiSelect
              values={selectedExtensions}
              options={fileTypes}
              column={extensionColumn}
              placeholder="Select file extensions..."
            />
          </>
        )}
      </div>

      <div className="mt-4">
        {typeColumn && (
          <>
            <p className="my-3 text-gray-600">Document Type</p>
            <FilterMultiSelect
              values={selectedTypes}
              options={docTypes}
              column={typeColumn}
              placeholder="Select document types..."
            />
          </>
        )}
      </div>

      <div className="mt-4">
        {issuesColumn && (
          <>
            <p className="my-3 text-gray-600">Document Issues</p>
            <FilterMultiSelect
              values={selectedIssues}
              options={issues}
              column={issuesColumn}
              placeholder="Select document issues..."
            />
          </>
        )}
      </div>

      {/* Relativity Metadata Filters - only show if columns are visible and have data */}
      {relativityIssuesColumn && relativityIssues.length > 0 && (
        <div className="mt-4">
          <div className="mb-3 flex items-center gap-2">
            <Icon className="lucide--database h-3.5 w-3.5 text-purple-600" />
            <p className="text-gray-600">Relativity Issues</p>
          </div>
          <FilterMultiSelect
            values={selectedRelativityIssues}
            options={relativityIssues}
            column={relativityIssuesColumn}
            placeholder="Select Relativity issues..."
          />
        </div>
      )}

      {/* Entities Filter */}
      {personsColumn && (
        <div className="mt-4">
          <p className="my-3 text-gray-600">Entities</p>
          <FilterMultiSelect
            values={selectedPersons}
            options={personNames}
            column={personsColumn}
            placeholder="Select entities..."
          />
        </div>
      )}

      {/* Run Date/Time Filter */}
      {uploadDateColumn && runDateTimes.length > 0 && (
        <div className="mt-4">
          <p className="my-3 text-gray-600">Run Date/Time</p>
          <FilterMultiSelect
            values={selectedRunDateTimes}
            options={runDateTimes}
            column={uploadDateColumn}
            placeholder="Select run date/times..."
          />
        </div>
      )}

      {/* Document Date Range - moved to end */}
      <div className="mt-4">
        <p className="my-3 text-gray-600">Document Date Range</p>
        <DateRangeFilter startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      </div>

      <div className="mt-4 flex w-full justify-end">
        <Button
          onClick={() => {
            setColumnFilters([]);
          }}
        >
          <Icon className="lucide--funnel-x" />
          <span>Clear All Filters</span>
        </Button>
      </div>
    </PopoverContent>
  );
}
